import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const val = parts.pop().split(';').shift();
    if (val) return val;
  }
  return localStorage.getItem(name) || '';
}

const getFormattedToday = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const calculateTimestamps = (filter, customFrom, customTo) => {
  const now = new Date();
  let start = new Date();
  let end = new Date();

  if (filter === 'today') {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (filter === 'yesterday') {
    start.setDate(now.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    end.setDate(now.getDate() - 1);
    end.setHours(23, 59, 59, 999);
  } else if (filter === '7days') {
    start.setDate(now.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (filter === 'custom' && customFrom && customTo) {
    start = new Date(`${customFrom}T00:00:00`);
    end = new Date(`${customTo}T23:59:59`);
  } else {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  }

  return {
    StartDate: Math.floor(start.getTime() / 1000),
    EndDate: Math.floor(end.getTime() / 1000),
  };
};

const ThirdPartyReport = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState('');
  const [apiKeyError, setApiKeyError] = useState('');
  const [posList, setPosList] = useState([]);
  const [selectedPos, setSelectedPos] = useState('');
  const [dateFilter, setDateFilter] = useState('today'); // 'today' | 'yesterday' | '7days' | 'custom'
  const [customFrom, setCustomFrom] = useState(getFormattedToday());
  const [customTo, setCustomTo] = useState(getFormattedToday());
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Initial load: Fetch API Key, then POS Connected List, then Report
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      const key = await fetchApiKey();
      const posId = await fetchPosList(key);
      if (key) {
        fetchReportData(posId || 14, key, 'today', customFrom, customTo);
      } else {
        setLoading(false);
      }
    };

    initData();
  }, []);

  // 1. Fetch API key from SubUserApiKeyOperation/SubUser/Get
  const fetchApiKey = async () => {
    try {
      const token = getCookie('token');
      const userid = getCookie('userid') || '10002';

      console.log('Fetching API key for UserId:', userid);
      const response = await axios.get(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserApiKeyOperation/SubUser/Get',
        {
          headers: {
            accept: 'text/plain',
            UserId: userid,
            'X-Authorization': token,
          },
        }
      );

      console.log('API Key Response:', response.data);

      if (
        response.data &&
        response.data.isresponse &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        const activeKeyObj = response.data.data.find((item) => item.active && item.apikey) || response.data.data[0];
        if (activeKeyObj && activeKeyObj.apikey) {
          setApiKey(activeKeyObj.apikey);
          setApiKeyError('');
          return activeKeyObj.apikey;
        }
      }

      setApiKey('');
      setApiKeyError('API key not found');
      return null;
    } catch (err) {
      console.error('Error fetching API Key:', err);
      setApiKey('');
      setApiKeyError('API key not found');
      return null;
    }
  };

  // 2. Fetch POS Connected List API: GET https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlReport/SubUser/ConnectedList?apikey={apikey}&subuserid={subuserid}
  const fetchPosList = async (currentApiKey) => {
    try {
      const key = currentApiKey || apiKey;
      const userid = getCookie('userid') || '10002';

      if (!key) {
        setPosList([]);
        return 14;
      }

      console.log(`Fetching Connected POS list: apikey=${key}, subuserid=${userid}`);
      const response = await axios.get(
        `https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlReport/SubUser/ConnectedList?apikey=${key}&subuserid=${userid}`,
        {
          headers: {
            accept: 'text/plain',
          },
        }
      );

      console.log('Connected POS list response:', response.data);

      const items = response.data?.list || response.data?.data || (Array.isArray(response.data) ? response.data : []);

      if (Array.isArray(items) && items.length > 0) {
        setPosList(items);
        const firstPos = items[0];
        const posId = firstPos.posid || firstPos.id || firstPos.value || 14;
        setSelectedPos(posId);
        return posId;
      }

      setPosList([]);
      setSelectedPos(14);
      return 14;
    } catch (err) {
      console.error('Error fetching connected POS list:', err);
      setPosList([]);
      setSelectedPos(14);
      return 14;
    }
  };

  // 3. Fetch Direct Report API: POST https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlReport/SubUser/Direct/Report
  const fetchReportData = async (
    posIdToUse = selectedPos,
    keyToUse = apiKey,
    filterToUse = dateFilter,
    fromToUse = customFrom,
    toToUse = customTo
  ) => {
    setLoading(true);
    setErrorMsg('');
    setApiKeyError('');

    let currentKey = keyToUse || apiKey;

    // Auto-fetch API key if missing
    if (!currentKey) {
      currentKey = await fetchApiKey();
    }

    if (!currentKey) {
      setLoading(false);
      setApiKeyError('API key not found');
      setReportData(null);
      return;
    }

    const posId = posIdToUse || selectedPos || 14;

    try {
      const userid = getCookie('userid') || '10002';
      const { StartDate, EndDate } = calculateTimestamps(filterToUse, fromToUse, toToUse);

      const payload = {
        posid: Number(posId),
        subuserid: Number(userid),
        msg: JSON.stringify({
          Action: 'Dashboard',
          Msg: JSON.stringify({
            StartDate: StartDate,
            EndDate: EndDate,
            TopSelling: 15,
            RecentInvoice: 15,
            IsAllTime: false,
          }),
        }),
        apikey: currentKey,
      };

      console.log('Sending Direct Report Payload:', payload);

      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlReport/SubUser/Direct/Report',
        payload,
        {
          headers: {
            accept: 'text/plain',
            'Content-Type': 'application/json-patch+json',
          },
        }
      );

      console.log('Direct Report Response:', response.data);

      if (response.data && response.data.isresponse) {
        let parsed = null;
        if (typeof response.data.data === 'string') {
          parsed = JSON.parse(response.data.data);
        } else {
          parsed = response.data.data;
        }

        if (parsed && parsed.IsResponse === false) {
          setErrorMsg(parsed.Error || 'Error fetching report.');
          setReportData(null);
        } else {
          setReportData(parsed);
        }
      } else {
        setErrorMsg(response.data?.errormsg || 'Failed to fetch report data.');
        setReportData(null);
      }
    } catch (err) {
      console.error('Error calling Direct Report API:', err);
      setErrorMsg('An error occurred while fetching the report.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePosChange = (e) => {
    const newPosId = e.target.value;
    setSelectedPos(newPosId);
    fetchReportData(newPosId, apiKey, dateFilter, customFrom, customTo);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    fetchReportData(selectedPos, apiKey, filter, customFrom, customTo);
  };

  const handleCustomDateSubmit = (e) => {
    e.preventDefault();
    fetchReportData(selectedPos, apiKey, 'custom', customFrom, customTo);
  };

  // Helper extraction
  const totalInvoiceObj = reportData?.TotalInvoice?.[0] || { InvoiceCount: 0, TotalAmount: 0 };
  const paymentModes = reportData?.PaymentModeWiseSales || [];
  const topSelling = reportData?.TopSelling || [];
  const recentInvoices = reportData?.RecentInvoice || [];
  const categoryWise = reportData?.CategoryWise || [];
  const hourlyData = reportData?.HourlyData || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f4f6f9', color: '#212529' }}>
      {/* Top Navbar Header */}
      <nav className="navbar navbar-expand navbar-light navbar-white bg-white m-0 px-3 py-2 border-bottom shadow-sm">
        <span className="navbar-brand font-weight-bold text-dark d-flex align-items-center" style={{ fontSize: '18px' }}>
          <i className="fas fa-chart-line text-success mr-2"></i>
          Report
        </span>

        <div className="form-inline ml-3">
          <label className="text-dark mr-2 font-weight-bold" htmlFor="posDropdown">
            Select POS:
          </label>
          <select
            id="posDropdown"
            className="form-control form-control-sm"
            style={{ minWidth: '160px', fontWeight: 'bold' }}
            value={selectedPos}
            onChange={handlePosChange}
          >
            {posList.length > 0 ? (
              posList.map((pos, idx) => {
                const id = pos.posid || pos.id || pos.value;
                const name = pos.posname || pos.name || pos.pos_name || pos.value || `POS ${id}`;
                return (
                  <option key={idx} value={id}>
                    {name} (ID: {id})
                  </option>
                );
              })
            ) : (
              <option value="14">POS ID: 14</option>
            )}
          </select>
        </div>

        <ul className="navbar-nav ml-auto d-flex align-items-center">
          <li className="nav-item mr-2">
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/')}>
              <i className="fas fa-store mr-1"></i>
              Merchant Panel
            </button>
          </li>
          <li className="nav-item">
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/select-option')}>
              <i className="fas fa-th-large mr-1"></i>
              Options
            </button>
          </li>
        </ul>
      </nav>

      {/* Date Filter & Control Bar */}
      <div className="bg-white border-bottom px-3 py-2 d-flex flex-wrap align-items-center justify-content-between">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="font-weight-bold mr-2 text-secondary">
            <i className="fas fa-calendar-alt mr-1"></i> Date Filter:
          </span>
          <div className="btn-group mr-3" role="group">
            <button
              type="button"
              className={`btn btn-sm ${dateFilter === 'today' ? 'btn-info font-weight-bold' : 'btn-outline-info'}`}
              onClick={() => handleDateFilterChange('today')}
            >
              Today
            </button>
            <button
              type="button"
              className={`btn btn-sm ${dateFilter === 'yesterday' ? 'btn-info font-weight-bold' : 'btn-outline-info'}`}
              onClick={() => handleDateFilterChange('yesterday')}
            >
              Yesterday
            </button>
            <button
              type="button"
              className={`btn btn-sm ${dateFilter === '7days' ? 'btn-info font-weight-bold' : 'btn-outline-info'}`}
              onClick={() => handleDateFilterChange('7days')}
            >
              7 Days
            </button>
            <button
              type="button"
              className={`btn btn-sm ${dateFilter === 'custom' ? 'btn-info font-weight-bold' : 'btn-outline-info'}`}
              onClick={() => setDateFilter('custom')}
            >
              Custom
            </button>
          </div>

          {dateFilter === 'custom' && (
            <form className="form-inline d-inline-flex align-items-center" onSubmit={handleCustomDateSubmit}>
              <label className="mr-1 text-sm font-weight-bold">From:</label>
              <input
                type="date"
                className="form-control form-control-sm mr-2"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                required
              />
              <label className="mr-1 text-sm font-weight-bold">To:</label>
              <input
                type="date"
                className="form-control form-control-sm mr-2"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-sm btn-primary">
                Apply Date
              </button>
            </form>
          )}
        </div>

        <div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fetchReportData(selectedPos, apiKey, dateFilter, customFrom, customTo)}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt mr-1 ${loading ? 'fa-spin' : ''}`}></i>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Main Body */}
      <div className="container-fluid p-4">
        {/* API Key Error Message */}
        {apiKeyError && (
          <div className="alert alert-danger font-weight-bold shadow-sm" role="alert">
            <i className="fas fa-exclamation-circle mr-2" style={{ fontSize: '18px' }}></i>
            {apiKeyError}
          </div>
        )}

        {/* General API Error Message */}
        {!apiKeyError && errorMsg && (
          <div className="alert alert-warning font-weight-bold shadow-sm" role="alert">
            <i className="fas fa-exclamation-triangle mr-2" style={{ fontSize: '18px' }}></i>
            {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="text-center my-5 py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="sr-only">Loading report...</span>
            </div>
            <h4 className="mt-3 text-secondary">Fetching Report Data...</h4>
          </div>
        ) : reportData ? (
          <>
            {/* KPI Summary Cards */}
            <div className="row">
              <div className="col-lg-3 col-6">
                <div className="small-box bg-info">
                  <div className="inner">
                    <h3>{totalInvoiceObj.InvoiceCount || 0}</h3>
                    <p>Total Invoices</p>
                  </div>
                  <div className="icon">
                    <i className="fas fa-file-invoice" />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-6">
                <div className="small-box bg-success">
                  <div className="inner">
                    <h3>₹{(totalInvoiceObj.TotalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                    <p>Total Sales Amount</p>
                  </div>
                  <div className="icon">
                    <i className="fas fa-rupee-sign" />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-6">
                <div className="small-box bg-warning">
                  <div className="inner">
                    <h3>{topSelling.length}</h3>
                    <p>Top Selling Products</p>
                  </div>
                  <div className="icon">
                    <i className="fas fa-box-open" />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-6">
                <div className="small-box bg-danger">
                  <div className="inner">
                    <h3>{categoryWise.length}</h3>
                    <p>Active Categories</p>
                  </div>
                  <div className="icon">
                    <i className="fas fa-tags" />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Mode Wise Sales & Hourly Data */}
            <div className="row mt-3">
              <div className="col-md-6 col-12">
                <div className="card card-primary card-outline shadow-sm">
                  <div className="card-header">
                    <h3 className="card-title font-weight-bold">
                      <i className="fas fa-credit-card mr-2 text-primary"></i>
                      Payment Mode Wise Sales
                    </h3>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-striped text-nowrap m-0">
                      <thead>
                        <tr>
                          <th>Payment Mode</th>
                          <th>Invoices</th>
                          <th>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentModes.length > 0 ? (
                          paymentModes.map((pm, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="badge badge-info p-2 font-weight-normal" style={{ fontSize: '13px' }}>
                                  {pm.Report || 'Default'}
                                </span>
                              </td>
                              <td className="font-weight-bold">{pm.InvoiceCount}</td>
                              <td className="text-success font-weight-bold">₹{pm.TotalAmount?.toFixed(2)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center py-3">
                              No payment mode data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-12">
                <div className="card card-info card-outline shadow-sm">
                  <div className="card-header">
                    <h3 className="card-title font-weight-bold">
                      <i className="fas fa-clock mr-2 text-info"></i>
                      Hourly Sales Breakdown
                    </h3>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-striped text-nowrap m-0">
                      <thead>
                        <tr>
                          <th>Hour</th>
                          <th>Total Sales Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hourlyData.length > 0 ? (
                          hourlyData.map((h, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="badge badge-secondary">{h.Hour}:00 HRS</span>
                              </td>
                              <td className="text-success font-weight-bold">₹{h.TotalAmount?.toFixed(2)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" className="text-center py-3">
                              No hourly data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Selling Items & Category Wise */}
            <div className="row mt-3">
              <div className="col-lg-7 col-12">
                <div className="card card-success card-outline shadow-sm">
                  <div className="card-header">
                    <h3 className="card-title font-weight-bold">
                      <i className="fas fa-fire mr-2 text-danger"></i>
                      Top Selling Items
                    </h3>
                  </div>
                  <div className="card-body p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-hover table-head-fixed text-nowrap m-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Item Name</th>
                          <th>Category</th>
                          <th>Qty Sold</th>
                          <th>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topSelling.length > 0 ? (
                          topSelling.map((item, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td className="font-weight-bold">{item.item_name}</td>
                              <td>
                                <span className="badge badge-light border">{item.categoryname}</span>
                              </td>
                              <td>
                                <span className="badge badge-primary">{item.TotalQty}</span>
                              </td>
                              <td className="text-success font-weight-bold">₹{item.TotalAmount?.toFixed(2)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-3">
                              No top selling items
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-lg-5 col-12">
                <div className="card card-warning card-outline shadow-sm">
                  <div className="card-header">
                    <h3 className="card-title font-weight-bold">
                      <i className="fas fa-th-list mr-2 text-warning"></i>
                      Category Wise Sales
                    </h3>
                  </div>
                  <div className="card-body p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-striped text-nowrap m-0">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Total Qty</th>
                          <th>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryWise.length > 0 ? (
                          categoryWise.map((cat, idx) => (
                            <tr key={idx}>
                              <td className="font-weight-bold">{cat.categoryname}</td>
                              <td>{cat.TotalQty}</td>
                              <td className="text-success font-weight-bold">₹{cat.TotalAmount?.toFixed(2)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center py-3">
                              No category data
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Invoices Table */}
            <div className="row mt-3">
              <div className="col-12">
                <div className="card card-dark card-outline shadow-sm">
                  <div className="card-header">
                    <h3 className="card-title font-weight-bold">
                      <i className="fas fa-history mr-2 text-info"></i>
                      Recent Invoices
                    </h3>
                  </div>
                  <div className="card-body p-0" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                    <table className="table table-hover table-striped text-nowrap m-0">
                      <thead>
                        <tr>
                          <th>Invoice ID</th>
                          <th>Bill ID</th>
                          <th>Payment Date</th>
                          <th>Order Type</th>
                          <th>Payment Mode</th>
                          <th>Subtotal</th>
                          <th>Tax Total</th>
                          <th>Grand Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentInvoices.length > 0 ? (
                          recentInvoices.map((inv, idx) => (
                            <tr key={idx}>
                              <td className="font-weight-bold">#{inv.id}</td>
                              <td>#{inv.bill_id}</td>
                              <td>{inv.payment_date || inv.Date?.split('T')[0]}</td>
                              <td>
                                <span className="badge badge-success">{inv.ordertype || 'Confirmed'}</span>
                              </td>
                              <td>
                                <span className="badge badge-info">{inv.remarks || 'N/A'}</span>
                              </td>
                              <td>₹{inv.sub_total?.toFixed(2)}</td>
                              <td>₹{inv.tax_total?.toFixed(2)}</td>
                              <td className="text-success font-weight-bold">₹{inv.grand_total?.toFixed(2)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-3">
                              No recent invoices found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          !apiKeyError && (
            <div className="text-center my-5 py-5">
              <h4 className="text-muted">No report data loaded. Please select a POS.</h4>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ThirdPartyReport;
